import React, { useEffect, useRef, useCallback, useState, useMemo } from "react";
import { Box, Button, CircularProgress, Typography, LinearProgress } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Auth } from "aws-amplify";
import * as routes from "../../../constants/routes";
import DashboardCard from "../DashboardCard";
// import ConfettiCelebration from "../../ConfettiCelebration";
import ModalReceiveGoal from "../../ModalReceiveGoal/ModalReceiveGoal";
import useGoalCelebration from "../../../hooks/useGoalCelebration";
import useFetch from "../../../hooks/useFetch";
import { useActivities } from "../../../context/ActivitiesContext";
import trophy from "../../../assets/images/SVG/trophy.svg";
import diaryChest from "../../../assets/images/SVG/diaryChest.svg";
import weeklyChest from "../../../assets/images/SVG/weeklyChest.svg";
import monthlyChest from "../../../assets/images/SVG/monthlyChest.svg";

const pulseGlow = keyframes`
  0%, 100% {
    transform: scale(1);
    text-shadow: 0 0 4px rgba(44, 173, 27, 0.3);
  }
  50% {
    transform: scale(1.1);
    text-shadow: 0 0 14px rgba(44, 173, 27, 0.6);
  }
`;

const GoalsList = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

const GoalItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "12px",
});

const GoalContent = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const GoalLabel = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "100%",
  letterSpacing: "0px",
  color: "#28293D",
  textTransform: "uppercase",
});

const GoalReward = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "10.05px",
  letterSpacing: "0px",
  color: "#6D716C",
});

const StyledProgress = styled(LinearProgress)({
  height: "14px",
  borderRadius: "7px",
  backgroundColor: "#e8e7fd",
  width: "100%",
  "& .MuiLinearProgress-bar": {
    backgroundColor: "#662D91",
    borderRadius: "7px",
  },
});

const CompletedBadge = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "12px",
  letterSpacing: "0px",
  color: "#2CAD1B",
});

const PulseRescuedText = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "12px",
  color: "#2CAD1B",
  animation: `${pulseGlow} 1.5s ease-in-out infinite`,
  display: "inline-block",
});

const InfoText = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 500,
  fontSize: "15px",
  lineHeight: "22px",
  letterSpacing: "0px",
  color: "#939393",
  marginBottom: "12px",
});

const GoalsAndRewardsCard = () => {
  const {
    refreshGamification,
    claimGoal,
    goalConfig,
    hasGoalData,
    isFetchingGoal,
    hasErrorGoal,
    claimingGoalId,
    claimGoalErrorId,
  } = useActivities();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeUser, session } = useSelector((state) => state.session);
  const autoClaimedRef = useRef({});
  const fetchedGoalUserRef = useRef(null);
  const goalUserSub = activeUser?.sub || session?.sub;

  const calculateProgress = (progress, goal) => {
    if (goal === 0) return 0;
    return Math.min((progress / goal) * 100, 100);
  };

  const goals = useMemo(() => [
    {
      id: "daily",
      label: t("global.daily_goal"),
      icon: diaryChest,
    },
    {
      id: "weekly",
      label: t("global.weekly_goal"),
      icon: weeklyChest,
    },
    {
      id: "monthly",
      label: t("global.monthly_goal"),
      icon: monthlyChest,
    },

  ].map(_goal => {
    return { ..._goal, ...goalConfig[_goal["id"]], reward: `+ ${goalConfig[_goal["id"]]["coins"]} ${t("global.crystals")}` }
  }), [goalConfig, t]);

  useEffect(() => {
    if (!goalUserSub || fetchedGoalUserRef.current === goalUserSub) return;

    fetchedGoalUserRef.current = goalUserSub;
    refreshGamification();
  }, [goalUserSub, refreshGamification]);

  const goalsContent = useMemo(() => {
    return goals.map((item) => {
      const completed = item.progress >= item.goal;
      const claimed = item.owned;
      const isClaiming = claimingGoalId === item.id;
      const hasClaimError = claimGoalErrorId === item.id;
      const progressPercent = calculateProgress(item.progress, item.goal);

      return (
        <GoalItem key={item.id}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "54px", minWidth: "54px" }}>
            <img src={item.icon} alt={item.label} width={54} height={54} />
          </Box>

          <GoalContent>
            <GoalLabel>{item.label}</GoalLabel>

            <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <Box sx={{ position: "relative", width: "55%", minWidth: "55%" }}>
                <StyledProgress
                  variant="determinate"
                  value={progressPercent}
                />
                <Typography
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: "6px",
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    fontSize: "9px",
                    fontWeight: 700,
                    color: progressPercent >= 85 ? "#ffffff" : "#662D91",
                    lineHeight: 1,
                  }}
                >
                  {item.progress}/{item.goal}
                </Typography>
              </Box>

              {isClaiming ? (
                <GoalReward sx={{ whiteSpace: "nowrap", marginLeft: "auto" }}>
                  {t("global.claiming_reward")}
                </GoalReward>
              ) : hasClaimError ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    marginLeft: "auto",
                  }}
                >
                  <Typography sx={{ color: "error.main", fontSize: "10px" }}>
                    {t("global.claim_reward_error")}
                  </Typography>
                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    onClick={(event) => {
                      event.stopPropagation();
                      claimGoal(item.id);
                    }}
                    sx={{ minWidth: "auto", padding: "2px 6px", fontSize: "9px" }}
                  >
                    {t("global.try_again")}
                  </Button>
                </Box>
              ) : completed && claimed ? (
                <PulseRescuedText sx={{ whiteSpace: "nowrap", marginLeft: "auto" }}>
                  {t("global.rescued") || t("global.claimed_past")}
                </PulseRescuedText>
              ) : completed ? (
                <CompletedBadge sx={{ whiteSpace: "nowrap", marginLeft: "auto" }}>
                  {t("global.completed")}
                </CompletedBadge>
              ) : (
                <GoalReward sx={{ whiteSpace: "nowrap", marginLeft: "auto" }}>{item.reward}</GoalReward>
              )}
            </Box>
          </GoalContent>
        </GoalItem>
      );
    })
  }, [claimGoal, claimGoalErrorId, claimingGoalId, goals, t])

  useEffect(() => {
    if (claimingGoalId || claimGoalErrorId) return;

    const goalToClaim = goals.find((item) => {
      const isCompleted = item.progress >= item.goal;
      const canClaim = !item.owned;
      return isCompleted && canClaim && !autoClaimedRef.current[item.id];
    });

    if (!goalToClaim) return;

    autoClaimedRef.current[goalToClaim.id] = true;
    claimGoal(goalToClaim.id);
  }, [claimGoal, claimGoalErrorId, claimingGoalId, goals]);



  const handleViewAll = () => {
    navigate(routes.R_ACHIEVEMENTS_GOALS);
  };

  const cardTitle = (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", textTransform: "none" }}>
      <img src={trophy} alt="trophy" style={{ width: "48px", height: "48px" }} />
      {t("global.goals")}
    </Box>
  );

  if (!hasGoalData && hasErrorGoal) {
    return (
      <DashboardCard title={cardTitle} sx={{ position: "relative", overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Typography color="text.secondary">
            {t("infos.unable_to_load_information_please_try_again_soon")}
          </Typography>
          <Button
            type="button"
            variant="outlined"
            disabled={isFetchingGoal}
            onClick={() => refreshGamification()}
          >
            {t("global.try_again")}
          </Button>
        </Box>
      </DashboardCard>
    );
  }

  if (!hasGoalData) {
    return (
      <DashboardCard title={cardTitle} sx={{ position: "relative", overflow: "hidden" }}>
        <Box sx={{ display: "flex", justifyContent: "center", padding: "24px" }}>
          <CircularProgress size={28} />
        </Box>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard
      title={cardTitle}
      onArrowClick={handleViewAll}
      onClick={handleViewAll}
      sx={{ position: "relative", overflow: "hidden" }}
    >
      <InfoText sx={{ fontSize: { xs: "11px", sm: "15px" } }}>
        {t("global.train_to_earn_crystals")}
      </InfoText>
      {goalsContent}
      <GoalsList>

      </GoalsList>
    </DashboardCard>
  );
};

export default GoalsAndRewardsCard;
